import { InputRule } from 'prosemirror-inputrules';
import { keymap } from 'prosemirror-keymap';
import { TextSelection } from 'prosemirror-state';
import schema from './schema';

const URL_TEST = /^https?:\/\/\S+$/;
const isUrl = (text) => URL_TEST.test(text);

// ─── Shared helper ────────────────────────────────────────────────────────────

// Returns { from, to } of the anchor mark surrounding $pos, or null.
const getMarkRange = ($pos, markType) => {
  const parent = $pos.parent;
  const start = $pos.start();
  let from = $pos.pos;
  let to = $pos.pos;
  let found = false;

  parent.forEach((child, offset) => {
    if (!child.isText) return;
    const childFrom = start + offset;
    const childTo = childFrom + child.nodeSize;
    if (child.marks.some(m => m.type === markType)) {
      if (childFrom <= $pos.pos && $pos.pos <= childTo) {
        from = childFrom;
        to = childTo;
        found = true;
      }
    }
  });

  return found ? { from, to } : null;
};

// Returns the doc position of the first anchor-marked text node at or after `from`.
const findNextLinkPos = (doc, from) => {
  const anchorMark = schema.marks.anchor;
  let result = null;
  doc.nodesBetween(from, doc.content.size, (node, pos) => {
    if (result !== null) return false;
    if (node.isText && node.marks.some(m => m.type === anchorMark)) {
      result = pos;
      return false;
    }
  });
  return result;
};

// Returns the doc position of the last anchor-marked text node that starts before `to`.
const findPrevLinkPos = (doc, to) => {
  const anchorMark = schema.marks.anchor;
  let result = null;
  doc.nodesBetween(0, to, (node, pos) => {
    if (node.isText && node.marks.some(m => m.type === anchorMark)) {
      result = pos; // keep overwriting — last match is nearest
    }
  });
  return result;
};

// ─── InputRule: auto-linkify on Space after a URL ─────────────────────────────

// `state` is the doc state BEFORE the typed char is inserted.
// `start`/`end` are positions in that pre-insertion state.
// We must re-insert the space in the same transaction because returning a
// transaction from handleTextInput suppresses PM's own char insertion.
const LINK_INPUT_REGEX = /https?:\/\/\S+\s$/;

export const linkInputRule = new InputRule(LINK_INPUT_REGEX, (state, match, start, end) => {
  const href = match[0].trimEnd();
  const anchorMark = schema.marks.anchor;
  return state.tr
    .addMark(start, end, anchorMark.create({ href, title: null }))
    .insertText(' ', end);
});

// ─── Paste handler ────────────────────────────────────────────────────────────

// If HTML is on the clipboard, yield to ProseMirror's built-in HTML parser so
// that <a href> tags in rich-text pastes (Google Docs, web pages, etc.) are
// correctly converted via the anchor mark's parseDOM rule.
// Only intercept plain-text-only pastes that look like a bare URL.
export const linkHandlePaste = (view, event) => {
  const html = event.clipboardData?.getData('text/html');
  if (html) return false; // let PM's HTML paste pipeline handle it

  const text = event.clipboardData?.getData('text/plain')?.trim();
  if (!text || !isUrl(text)) return false;

  const { state } = view;
  const { selection } = state;
  const anchorMark = schema.marks.anchor;
  const mark = anchorMark.create({ href: text, title: null });

  if (!selection.empty) {
    // Wrap the current selection as a link to the pasted URL
    const { $from, $to } = selection;
    view.dispatch(
      state.tr
        .removeMark($from.pos, $to.pos, anchorMark)
        .addMark($from.pos, $to.pos, mark)
        .scrollIntoView()
    );
    return true;
  }

  // No selection — insert the URL as linked text at the cursor
  const from = selection.from;
  view.dispatch(
    state.tr
      .insertText(text, from)
      .addMark(from, from + text.length, mark)
      .scrollIntoView()
  );
  return true;
};

// ─── Keyboard plugin: Tab/Shift-Tab link navigation + Backspace to unlink ────

// Walk up the ancestor chain to detect any enclosing list item node.
// $cursor.parent is always the immediate parent (usually `paragraph`), so a
// direct name check misses list items — we must walk depth.
const isInListItem = ($cursor) => {
  for (let d = $cursor.depth; d >= 0; d--) {
    const name = $cursor.node(d).type.name;
    if (name === 'list_item' || name === 'check_list_item') return true;
  }
  return false;
};

export const linkKeyboardPlugin = keymap({
  // Tab: jump to the next link in the document (wraps around).
  // Yields to the list-indent handler when cursor is inside a list item.
  'Tab': (state, dispatch) => {
    const { $cursor } = state.selection;
    if (!$cursor) return false;
    if (isInListItem($cursor)) return false;

    const anchorMark = schema.marks.anchor;
    const range = getMarkRange($cursor, anchorMark);
    // Start search after the end of the current link (or one step ahead if no link)
    const searchFrom = range ? range.to : $cursor.pos + 1;

    let nextPos = findNextLinkPos(state.doc, searchFrom);

    // Wrap around to the first link in the doc
    if (nextPos === null) {
      const firstPos = findNextLinkPos(state.doc, 0);
      // Don't wrap to the link we're already on
      if (firstPos !== null && !(range && firstPos >= range.from && firstPos < range.to)) {
        nextPos = firstPos;
      }
    }

    if (nextPos === null) return false; // no links at all — let Tab fall through

    if (dispatch) {
      dispatch(state.tr.setSelection(TextSelection.create(state.doc, nextPos)));
    }
    return true;
  },

  // Shift-Tab: jump to the previous link (wraps around).
  // Yields to the list-outdent handler when cursor is inside a list item.
  'Shift-Tab': (state, dispatch) => {
    const { $cursor } = state.selection;
    if (!$cursor) return false;
    if (isInListItem($cursor)) return false;

    const anchorMark = schema.marks.anchor;
    const range = getMarkRange($cursor, anchorMark);
    const searchTo = range ? range.from : $cursor.pos;

    let prevPos = findPrevLinkPos(state.doc, searchTo);

    // Wrap around to the last link in the doc
    if (prevPos === null) {
      const lastPos = findPrevLinkPos(state.doc, state.doc.content.size);
      if (lastPos !== null && !(range && lastPos >= range.from && lastPos < range.to)) {
        prevPos = lastPos;
      }
    }

    if (prevPos === null) return false;

    if (dispatch) {
      dispatch(state.tr.setSelection(TextSelection.create(state.doc, prevPos)));
    }
    return true;
  },

  // Backspace: when the cursor is at the very start of a link, remove the
  // anchor mark (keeping the text) instead of deleting a character. Acts as a
  // quick keyboard shortcut to unlink without opening a dialog.
  'Backspace': (state, dispatch) => {
    const { $cursor } = state.selection;
    if (!$cursor) return false;

    const anchorMark = schema.marks.anchor;
    const range = getMarkRange($cursor, anchorMark);
    if (!range || $cursor.pos !== range.from) return false;

    if (dispatch) {
      dispatch(state.tr.removeMark(range.from, range.to, anchorMark));
    }
    return true;
  },
});
